<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateBrokerInvestmentDetailHeader from '../components/RealEstateBrokerInvestmentDetailHeader.vue';
import RealEstateBrokerInvestmentDetailState from '../components/RealEstateBrokerInvestmentDetailState.vue';
import RealEstateBrokerInvestmentDetailSummary from '../components/RealEstateBrokerInvestmentDetailSummary.vue';
import RealEstateBrokerInvestmentSuggestions from '../components/RealEstateBrokerInvestmentSuggestions.vue';
import { useRealEstateBrokerInvestmentDetail } from '../composables/useRealEstateBrokerInvestmentDetail';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const state = useRealEstateBrokerInvestmentDetail({
  investmentId: computed(() => route.params.investmentId),
  notify: useAlert,
  t,
});
const investment = computed(() => state.detail.value?.investment);
const suggestions = computed(() => state.detail.value?.suggestions || []);
const accountParams = computed(() => ({ accountId: route.params.accountId }));
const goBack = () =>
  router.push({
    name: 'operacao_imobiliaria_corretor_investimentos',
    params: accountParams.value,
  });
const hasRegionalSuggestions = computed(() =>
  ['frio', 'morno'].includes(investment.value?.temperatura_calculada)
);
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <LaisHeader
      :title="t('REAL_ESTATE_BROKER_INVESTMENT.DETAIL.TITLE')"
      :subtitle="t('REAL_ESTATE_BROKER_INVESTMENT.DETAIL.SUBTITLE')"
      :breadcrumb="[
        { label: t('REAL_ESTATE_BROKER_DASHBOARD.TITLE') },
        { label: t('REAL_ESTATE_BROKER_INVESTMENT.TITLE') },
        { label: t('REAL_ESTATE_BROKER_INVESTMENT.DETAIL.TITLE') },
      ]"
      show-back
      :back-label="t('REAL_ESTATE_BROKER_INVESTMENT.DETAIL.BACK')"
      @back="goBack"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="state.loading.value"
          @click="state.load"
        >
          {{ t('REAL_ESTATE_BROKER_INVESTMENT.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>
    <main
      class="mx-auto flex w-full max-w-[1120px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <RealEstateBrokerInvestmentDetailState
        v-if="state.loading.value || state.error.value"
        :loading="state.loading.value"
        :error="state.error.value"
      />
      <template v-else-if="investment">
        <RealEstateBrokerInvestmentDetailHeader :investment="investment" />
        <RealEstateBrokerInvestmentDetailSummary :investment="investment" />
        <RealEstateBrokerInvestmentSuggestions
          v-if="hasRegionalSuggestions"
          :suggestions="suggestions"
        />
      </template>
    </main>
  </div>
</template>
